import { LOCAL_STORAGE, PLAN } from './constants';

export const PROFILE_VALIDATION_STATUS = {
  BLOCKED: 'blocked',
  FETCHING: 'fetching',
  REJECTED: 'rejected',
  NONE: 'none',
};

export const PROFILE_VALIDATION_POLL_MS = 20000;

const CONTRACT_STATUSES = new Set(
  Object.values(PROFILE_VALIDATION_STATUS)
);

export const isDrMissing = (domain) =>
  domain?.metrics?.authority?.dr == null;

const isProPlan = (profile) => profile?.plan === PLAN.PRO;

/** The "Choose your plan" step was answered (or they're Pro). */
export const isPlanStepDone = (profile) =>
  profile?.plan === PLAN.PRO ||
  localStorage.getItem(LOCAL_STORAGE.PLAN_STEP_DONE) === '1';

const getDomainList = (profile, domains) => {
  if (Array.isArray(profile?.domains) && profile.domains.length > 0) {
    return profile.domains;
  }
  return Array.isArray(domains) ? domains : [];
};

/**
 * Drive routing from POST /users/get-profile (`plan` + `profileValidation.status`).
 *
 * New contract: blocked | fetching | rejected | none.
 * Until that ships, legacy `pending` maps to a hard wall for Free and into
 * the app for Pro. `opportunitiesPreview` is ignored.
 */
export const getProfileValidationStatus = (profile, domains = []) => {
  const raw = profile?.profileValidation?.status;
  const domainList = getDomainList(profile, domains);

  if (CONTRACT_STATUSES.has(raw)) {
    return raw;
  }

  if (raw === 'pending') {
    return isProPlan(profile)
      ? PROFILE_VALIDATION_STATUS.NONE
      : PROFILE_VALIDATION_STATUS.BLOCKED;
  }

  if (!isProPlan(profile)) {
    const hasApproved = domainList.some((d) => d.status === 'APPROVED');
    const awaitingReview = domainList.some(
      (d) => d.status === 'TO_REVIEW' || d.status === 'TO_RE_REVIEW'
    );
    const rejected = domainList.some(
      (d) => d.status === 'REJECTED' || d.status === 'BANNED'
    );
    if (hasApproved) {
      return PROFILE_VALIDATION_STATUS.NONE;
    }
    if (rejected && !awaitingReview) {
      return PROFILE_VALIDATION_STATUS.REJECTED;
    }
    if (awaitingReview) {
      return PROFILE_VALIDATION_STATUS.BLOCKED;
    }
  }

  return PROFILE_VALIDATION_STATUS.NONE;
};

export const shouldShowFetchingBanner = (profile, domains = []) =>
  getProfileValidationStatus(profile, domains) ===
    PROFILE_VALIDATION_STATUS.FETCHING;

export const isRejectedValidation = (profile, domains = []) =>
  getProfileValidationStatus(profile, domains) ===
  PROFILE_VALIDATION_STATUS.REJECTED;

export const shouldPollProfileValidation = (profile, domains = []) => {
  const status = getProfileValidationStatus(profile, domains);
  return (
    status === PROFILE_VALIDATION_STATUS.BLOCKED ||
    status === PROFILE_VALIDATION_STATUS.FETCHING
  );
};
